import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaGift,
  FaHome,
  FaSyncAlt,
  FaTimes,
  FaTimesCircle,
  FaUserClock,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function cleanParam(value) {
  const s = String(value ?? "").trim();
  if (!s || s === "null" || s === "undefined") return "";
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

//Created By Cursor
function dateFromYearMonth(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, 1)
    : new Date();
  d.setDate(1);
  return d;
}

//Created By Cursor
function formatIso(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  const mm = m > 9 ? String(m) : `0${m}`;
  const dd = d > 9 ? String(d) : `0${d}`;
  return {
    iso: `${y}-${mm}-${dd}`,
    display: `${dd}-${mm}-${y}`,
    month: mm,
    year: String(y),
  };
}

//Created By Cursor
function parseHolidays(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

//Created By Cursor
function dateParts(iso) {
  if (!iso) return { day: "—", weekday: "", monthShort: "", year: "", full: "—" };
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return { day: iso, weekday: "", monthShort: "", year: "", full: iso };
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const dd = d.padStart(2, "0");
  const mm = m.padStart(2, "0");
  return {
    day: String(Number(d)),
    weekday: date.toLocaleString("en-US", { weekday: "short" }),
    weekdayFull: date.toLocaleString("en-US", { weekday: "long" }),
    monthShort: date.toLocaleString("en-US", { month: "short" }),
    year: y,
    full: `${dd}-${mm}-${y}`,
  };
}

//Created By Cursor
function todayIso() {
  return formatIso(new Date()).iso;
}

//Created By Cursor
function isSundayDate(iso) {
  if (!iso) return false;
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return false;
  return new Date(y, m - 1, d).getDay() === 0;
}

//Created By Cursor
function attendanceLabel(row) {
  const status = String(row?.status || "").trim();
  const iso = row?.attendence_date || row?.attendance_date || "";
  const mark = String(row?.attendence || "").toUpperCase();
  const sunday = /^sunday$/i.test(status) || isSundayDate(iso);
  const isFuture = !!iso && iso > todayIso();

  if (/^taken$/i.test(status)) {
    if (mark === "A") return { text: "Absent", tone: "a", mark: "A" };
    if (mark === "L") return { text: "Leave", tone: "l", mark: "L" };
    return { text: "Present", tone: "p", mark: "P" };
  }
  if (/^holiday$/i.test(status)) {
    const holidays = parseHolidays(row?.holidays);
    const occasion =
      holidays[0]?.holiday_Occasion ||
      holidays[0]?.holiday_occasion ||
      "Holiday";
    return { text: occasion, tone: "holiday", mark: "" };
  }
  //Created By Cursor — any future date (incl. Sunday) stays Upcoming, no Record
  if (isFuture || /^upcoming$/i.test(status)) {
    return {
      text: "Upcoming",
      tone: "upcoming",
      mark: "",
      isSunday: sunday,
    };
  }
  //Created By Cursor — today/past Sundays remain recordable
  if (sunday) {
    return { text: "Not Taken", tone: "pending", mark: "", isSunday: true };
  }
  if (/^not\s*taken$/i.test(status))
    return { text: "Not Taken", tone: "pending", mark: "" };
  return { text: status || "—", tone: "pending", mark: "" };
}

//Created By Cursor
function KpiChip({ tone, icon: Icon, label, value }) {
  return (
    <div className={`sm-kpi-chip sm-kpi-chip--${tone}`}>
      <span className="sm-kpi-chip__icon" aria-hidden>
        {Icon ? <Icon /> : null}
      </span>
      <div className="sm-kpi-chip__text">
        <span className="sm-kpi-chip__label">{label}</span>
        <span className="sm-kpi-chip__value">{value}</span>
      </div>
    </div>
  );
}

//Created By Cursor
export default function StudentAttendanceDetail() {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const loggedIn = localStorage.getItem("token") != null;

  const classId = params.class_id;
  const sectionId = params.section_id;
  const enrollmentNo = cleanParam(params.enrollment_no);
  const className = cleanParam(params.class_name);
  const sectionName = cleanParam(params.section_name);
  const studentName = cleanParam(params.stu_name) || "Student";
  const classLabel = sectionName ? `${className}:${sectionName}` : className;

  const [currentDate, setCurrentDate] = useState(() => {
    const q = new URLSearchParams(location.search).get("month");
    return dateFromYearMonth(q);
  });
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [filter, setFilter] = useState("all");
  const [recordingDate, setRecordingDate] = useState("");
  const [pendingMark, setPendingMark] = useState("P");
  const [saving, setSaving] = useState(false);
  const [showRecordAll, setShowRecordAll] = useState(false);
  const [bulkMarks, setBulkMarks] = useState({});
  const [submittingAll, setSubmittingAll] = useState(false);
  const busy = loading || saving || submittingAll;

  const monthParts = useMemo(() => formatIso(currentDate), [currentDate]);
  const monthLabel = currentDate.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  const pendingDates = useMemo(() => {
    return attendanceData.filter((row) => {
      const info = attendanceLabel(row);
      return info.tone === "pending";
    });
  }, [attendanceData]);

  const liveCounts = useMemo(() => {
    let present = 0;
    let absent = 0;
    let leave = 0;
    let pending = 0;
    let off = 0;
    attendanceData.forEach((row) => {
      const info = attendanceLabel(row);
      if (info.tone === "p") present += 1;
      else if (info.tone === "a") absent += 1;
      else if (info.tone === "l") leave += 1;
      else if (info.tone === "pending") pending += 1;
      else if (info.tone === "holiday") off += 1;
    });
    return {
      present,
      absent,
      leave,
      pending,
      off,
      taken: present + absent + leave,
    };
  }, [attendanceData]);

  const filteredRows = useMemo(() => {
    if (filter === "all") return attendanceData;
    return attendanceData.filter((row) => {
      const info = attendanceLabel(row);
      if (filter === "p") return info.tone === "p";
      if (filter === "a") return info.tone === "a";
      if (filter === "l") return info.tone === "l";
      if (filter === "pending") return info.tone === "pending";
      if (filter === "off") return info.tone === "holiday";
      return true;
    });
  }, [attendanceData, filter]);

  const loadMonth = useCallback(async () => {
    if (!classId || !sectionId || !enrollmentNo) return;
    setLoading(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const reqParams = {
        school_code: schoolCode,
        enrollment_no: enrollmentNo,
        class_id: classId,
        section_id: sectionId,
        month: monthParts.month,
        year: monthParts.year,
      };
      const url = `${API_BASE()}getParticularStudentAttendenceMonth.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(reqParams)}`);
      const response = await axios
        .get(url, { params: reqParams })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        setAttendanceData(Array.isArray(response.data) ? response.data : []);
      } else {
        setAttendanceData([]);
        myToast.notify_warning(
          response?.api_remark || "Failed to load student attendance."
        );
      }
    } catch (error) {
      console.error(error);
      setAttendanceData([]);
      myToast.notify_warning("Failed to load student attendance.");
    } finally {
      setLoading(false);
    }
  }, [classId, sectionId, enrollmentNo, monthParts.month, monthParts.year]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  useEffect(() => {
    setFilter("all");
    setRecordingDate("");
    setPendingMark("P");
    setShowRecordAll(false);
    setBulkMarks({});
  }, [monthParts.month, monthParts.year]);

  const openRecordAll = () => {
    if (!pendingDates.length) {
      myToast.notify_warning("No unrecorded dates in this month.");
      return;
    }
    const next = {};
    pendingDates.forEach((row) => {
      const iso = row.attendance_date || row.attendence_date;
      if (iso) next[iso] = "P";
    });
    setBulkMarks(next);
    setShowRecordAll(true);
    setRecordingDate("");
  };

  const closeRecordAll = () => {
    if (submittingAll) return;
    setShowRecordAll(false);
  };

  const setAllBulkMark = (mark) => {
    setBulkMarks((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((iso) => {
        next[iso] = mark;
      });
      return next;
    });
  };

  const setBulkMark = (iso, mark) => {
    setBulkMarks((prev) => ({ ...prev, [iso]: mark }));
  };

  const startRecording = (iso) => {
    setRecordingDate(iso);
    setPendingMark("P");
  };

  const cancelRecording = () => {
    setRecordingDate("");
    setPendingMark("P");
  };

  const postOneAttendance = async (iso, mark, staffId, schoolCode) => {
    const body = new URLSearchParams();
    body.append("class_id", classId);
    body.append("section_id", sectionId);
    body.append("date", iso);
    body.append(
      "attendence",
      JSON.stringify([
        {
          student_enrollment_no: enrollmentNo,
          attendence: mark,
        },
      ])
    );
    body.append("staff_id", staffId);
    body.append("school_code", schoolCode);
    const url = `${API_BASE()}addStudentAttendence.php`;
    const response = await axios.post(url, body).then((res) => res.data);
    return response;
  };

  const submitAttendance = async (iso) => {
    if (!iso || !pendingMark) {
      myToast.notify_warning("Select Present, Absent or Leave.");
      return;
    }
    const staff = SessionManager.getStaffDetails();
    const staffId = staff?.staff_id;
    if (!staffId) {
      myToast.notify_warning("Missing staff details.");
      return;
    }

    setSaving(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      console.log("Full URL: ", `${API_BASE()}addStudentAttendence.php`);
      const response = await postOneAttendance(
        iso,
        pendingMark,
        staffId,
        schoolCode
      );

      if (response?.api_status === "OK") {
        myToast.notify_success("Attendance recorded.");
        setRecordingDate("");
        setPendingMark("P");
        await loadMonth();
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to save attendance."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to save attendance.");
    } finally {
      setSaving(false);
    }
  };

  const submitAllAttendance = async () => {
    const entries = Object.entries(bulkMarks).filter(([iso, mark]) => iso && mark);
    if (!entries.length) {
      myToast.notify_warning("No dates to submit.");
      return;
    }
    const staff = SessionManager.getStaffDetails();
    const staffId = staff?.staff_id;
    if (!staffId) {
      myToast.notify_warning("Missing staff details.");
      return;
    }

    setSubmittingAll(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const payload = entries.map(([date, attendence]) => ({
        date,
        attendence,
      }));
      const body = new URLSearchParams();
      body.append("class_id", classId);
      body.append("section_id", sectionId);
      body.append("student_enrollment_no", enrollmentNo);
      body.append("attendence", JSON.stringify(payload));
      body.append("staff_id", staffId);
      body.append("school_code", schoolCode);

      const url = `${API_BASE()}addStudentAttendenceMultiDate.php`;
      console.log("Full URL: ", url);
      const response = await axios.post(url, body).then((res) => res.data);

      if (response?.api_status === "OK") {
        const inserted = Number(response?.data?.inserted || 0);
        const skipped = Number(response?.data?.skipped || 0);
        if (inserted > 0) {
          myToast.notify_success(
            `Recorded ${inserted} day${inserted === 1 ? "" : "s"}${
              skipped ? ` · skipped ${skipped}` : ""
            }.`
          );
        } else {
          myToast.notify_warning(
            response?.api_remark || "Nothing new was recorded."
          );
        }
        setShowRecordAll(false);
        setBulkMarks({});
        await loadMonth();
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to submit attendance."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to submit attendance.");
    } finally {
      setSubmittingAll(false);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      d.setDate(1);
      return d;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      d.setDate(1);
      const now = new Date();
      if (
        d.getFullYear() > now.getFullYear() ||
        (d.getFullYear() === now.getFullYear() &&
          d.getMonth() > now.getMonth())
      ) {
        return prev;
      }
      return d;
    });
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  const filterTabs = [
    { key: "all", label: "All", count: attendanceData.length },
    { key: "p", label: "Present", count: liveCounts.present },
    { key: "a", label: "Absent", count: liveCounts.absent },
    { key: "l", label: "Leave", count: liveCounts.leave },
    { key: "pending", label: "Pending", count: liveCounts.pending },
    { key: "off", label: "Off", count: liveCounts.off },
  ];

  return (
    <div
      className="content payroll-page sm-page tat-page sat-page sat-detail-page"
      style={{ fontFamily: PAGE_FONT, background: "#ffffff" }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Attendance</span>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Attendance Student</span>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>{studentName}</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell sat-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaCalendarAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">{studentName}</h1>
              <p className="sm-hero__sub">
                {classLabel || "Class"} · {monthLabel}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadMonth}
              disabled={busy}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="sat-body">
          <div className="sat-meta-card">
            <div className="sm-kpi-row sat-meta-chips" aria-label="Month totals">
              <KpiChip
                tone="mint"
                icon={FaCheckCircle}
                label="Present"
                value={liveCounts.present}
              />
              <KpiChip
                tone="sand"
                icon={FaTimesCircle}
                label="Absent"
                value={liveCounts.absent}
              />
              <KpiChip
                tone="blue"
                icon={FaUserClock}
                label="Leave"
                value={liveCounts.leave}
              />
              <KpiChip
                tone="sky"
                icon={FaCalendarAlt}
                label="Recorded"
                value={liveCounts.taken}
              />
            </div>
          </div>

          <div className="sat-month-nav">
            <button
              type="button"
              className="sat-month-nav__btn"
              onClick={handlePrevMonth}
              disabled={loading}
              aria-label="Previous month"
            >
              <FaChevronLeft />
            </button>
            <div className="sat-month-nav__center">
              <strong>{monthLabel}</strong>
              <small>
                {classLabel || "Class"} · {attendanceData.length} days
              </small>
            </div>
            <button
              type="button"
              className="sat-month-nav__btn"
              onClick={handleNextMonth}
              disabled={loading}
              aria-label="Next month"
            >
              <FaChevronRight />
            </button>
          </div>

          {loading && !attendanceData.length ? (
            <div className="sat-loading">
              <MyLoader />
            </div>
          ) : attendanceData.length === 0 ? (
            <div className="sat-empty-card">
              <span className="sat-empty-card__icon" aria-hidden>
                <FaCalendarAlt />
              </span>
              <h2>No dates found</h2>
              <p>No attendance days available for {monthLabel}.</p>
            </div>
          ) : (
            <section className="sat-date-list-card">
              <div className="sat-date-list-card__head">
                <div className="min-w-0">
                  <h2>Attendance by date</h2>
                  <p>All days in {monthLabel} with recorded status</p>
                </div>
                <div className="sat-date-list-card__tools">
                  <button
                    type="button"
                    className="sat-record-all-btn"
                    disabled={busy || pendingDates.length === 0}
                    onClick={openRecordAll}
                  >
                    Record All
                    {pendingDates.length ? (
                      <b>{pendingDates.length}</b>
                    ) : null}
                  </button>
                  <span className="sat-date-list-card__count">
                    {filteredRows.length}
                    {filter !== "all" ? ` / ${attendanceData.length}` : ""}
                  </span>
                </div>
              </div>

              <div
                className="sat-date-filters"
                role="tablist"
                aria-label="Filter dates"
              >
                {filterTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={filter === tab.key}
                    className={`sat-date-filters__btn${
                      filter === tab.key ? " is-active" : ""
                    } is-${tab.key}`}
                    onClick={() => setFilter(tab.key)}
                  >
                    {tab.label}
                    <b>{tab.count}</b>
                  </button>
                ))}
              </div>

              {filteredRows.length === 0 ? (
                <div className="sat-empty sat-empty--soft">
                  No days in this filter.
                </div>
              ) : (
                <div className="sat-date-list" role="list">
                  {filteredRows.map((row) => {
                    const iso = row.attendance_date || row.attendence_date;
                    const parts = dateParts(iso);
                    const info = attendanceLabel(row);
                    const canRecord = info.tone === "pending";
                    const isRecording = recordingDate === iso;
                    return (
                      <div
                        key={iso}
                        role="listitem"
                        className={`sat-date-row is-${info.tone}${
                          isRecording ? " is-recording" : ""
                        }`}
                      >
                        <span
                          className={`sat-date-row__day is-${info.tone}`}
                          aria-hidden
                        >
                          {parts.day}
                        </span>
                        <div className="sat-date-row__date">
                          <div className="sat-date-row__title">
                            <strong className="sat-date-row__cal">
                              {parts.full}
                            </strong>
                            <span
                              className={`sat-date-row__weekday is-${info.tone}`}
                            >
                              {parts.weekday}
                            </span>
                          </div>
                          <small>
                            {parts.weekdayFull}
                            {parts.monthShort
                              ? ` · ${parts.day} ${parts.monthShort} ${parts.year}`
                              : ""}
                            {info.tone === "holiday" ? ` · ${info.text}` : ""}
                          </small>
                          {isRecording ? (
                            <div className="sat-date-row__record">
                              <div
                                className="sat-date-row__marks"
                                role="group"
                                aria-label="Mark attendance"
                              >
                                {[
                                  { key: "P", label: "Present" },
                                  { key: "A", label: "Absent" },
                                  { key: "L", label: "Leave" },
                                ].map((opt) => (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    className={`tat-mark-pill tat-mark-pill--status tat-mark-pill--${opt.key.toLowerCase()}${
                                      pendingMark === opt.key
                                        ? " is-active"
                                        : ""
                                    }`}
                                    disabled={busy}
                                    onClick={() => setPendingMark(opt.key)}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                              <div className="sat-date-row__record-actions">
                                <button
                                  type="button"
                                  className="sat-date-row__save"
                                  disabled={busy}
                                  onClick={() => submitAttendance(iso)}
                                >
                                  {saving ? "Saving…" : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="sat-date-row__cancel"
                                  disabled={busy}
                                  onClick={cancelRecording}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        {isRecording ? null : canRecord ? (
                          <button
                            type="button"
                            className="sat-date-row__record-btn"
                            disabled={busy}
                            onClick={() => startRecording(iso)}
                          >
                            Record
                          </button>
                        ) : (
                          <span
                            className={`sat-date-row__badge is-${info.tone}`}
                          >
                            {info.mark ? (
                              <>
                                <span className="sat-stat-tick" aria-hidden>
                                  ✓
                                </span>
                                <em>{info.mark}</em>
                                <b>{info.text}</b>
                              </>
                            ) : info.tone === "holiday" ? (
                              <>
                                <FaGift aria-hidden /> Holiday
                              </>
                            ) : (
                              info.text
                            )}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {showRecordAll
        ? createPortal(
            <div className="sat-bulk-modal" role="dialog" aria-modal="true">
              <button
                type="button"
                className="sat-bulk-modal__backdrop"
                aria-label="Close"
                disabled={submittingAll}
                onClick={closeRecordAll}
              />
              <div
                className="sat-bulk-modal__panel"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="sat-bulk-modal__hero">
                  <div className="sat-bulk-modal__hero-icon" aria-hidden>
                    <FaCheckCircle />
                  </div>
                  <div className="min-w-0">
                    <h3>Record All</h3>
                    <p>
                      {studentName} · {monthLabel} · {pendingDates.length}{" "}
                      unrecorded day
                      {pendingDates.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="sat-bulk-modal__close"
                    disabled={submittingAll}
                    aria-label="Close"
                    onClick={closeRecordAll}
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="sat-bulk-modal__toolbar">
                  <span>Set all</span>
                  {[
                    { key: "P", label: "Present" },
                    { key: "A", label: "Absent" },
                    { key: "L", label: "Leave" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      className={`tat-mark-pill tat-mark-pill--status tat-mark-pill--${opt.key.toLowerCase()}`}
                      disabled={submittingAll}
                      onClick={() => setAllBulkMark(opt.key)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="sat-bulk-modal__body">
                  {pendingDates.map((row) => {
                    const iso = row.attendance_date || row.attendence_date;
                    const parts = dateParts(iso);
                    const mark = bulkMarks[iso] || "P";
                    return (
                      <div key={iso} className="sat-bulk-row">
                        <div className="sat-bulk-row__date">
                          <strong>{parts.full}</strong>
                          <small>
                            {parts.weekdayFull}
                            {parts.monthShort
                              ? ` · ${parts.day} ${parts.monthShort}`
                              : ""}
                          </small>
                        </div>
                        <div
                          className="sat-bulk-row__marks"
                          role="group"
                          aria-label={`Mark ${parts.full}`}
                        >
                          {[
                            { key: "P", label: "P" },
                            { key: "A", label: "A" },
                            { key: "L", label: "L" },
                          ].map((opt) => (
                            <button
                              key={opt.key}
                              type="button"
                              className={`tat-mark-pill tat-mark-pill--status tat-mark-pill--${opt.key.toLowerCase()}${
                                mark === opt.key ? " is-active" : ""
                              }`}
                              disabled={submittingAll}
                              onClick={() => setBulkMark(iso, opt.key)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="sat-bulk-modal__foot">
                  <button
                    type="button"
                    className="sat-bulk-modal__cancel"
                    disabled={submittingAll}
                    onClick={closeRecordAll}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sat-bulk-modal__submit"
                    disabled={submittingAll || !pendingDates.length}
                    onClick={submitAllAttendance}
                  >
                    {submittingAll
                      ? "Submitting…"
                      : `Submit All (${pendingDates.length})`}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
